import { generatePath } from 'react-router-dom'
import { CompanyTag } from '@/components/ui/badge'
import { GridCellProps, GridColumnProps } from '@progress/kendo-react-grid'
import { DealDtoKey } from 'api/Clients/Types/DealDto'
import { HeaderSortCell } from 'components/Filter'
import { inputFilterCell } from 'components/Filter/FilterCell'
import { Link } from 'components/Link'
import { formatLocalDateUTC } from 'utils/date'
import { links } from 'utils/featureToggles'

const InputFilterCell = inputFilterCell({
  options: {
    operator: '==',
  },
})

export interface GridColumn extends GridColumnProps {
  show: boolean
  width?: number
}

export const dealsColumns: GridColumn[] = [
  {
    title: 'Trade',
    field: DealDtoKey.ticket,
    width: 100,
    minResizableWidth: 100,
    show: true,
    filterable: false,
    sortable: false,
  },
  {
    title: 'State',
    field: DealDtoKey.state,
    className: 'table-columns__content',
    filter: 'text',
    width: 90,
    minResizableWidth: 90,
    show: true,
    filterable: false,
    sortable: false,
  },
  {
    title: 'IB Code',
    field: DealDtoKey.introducingBroker,
    filterCell: InputFilterCell,
    cell: (props: GridCellProps): JSX.Element => {
      const { introducingBroker } = props.dataItem

      if (!introducingBroker?.id) {
        return <td />
      }

      return (
        <td style={{ color: 'var(--link)', textDecoration: 'underline' }}>
          <Link
            to={{
              pathname: generatePath(links.recordsIntroducingBrokers.detailsUrl as string, {
                id: introducingBroker.id,
                slug: 'details',
              }),
            }}
          >
            {introducingBroker?.name}
          </Link>
        </td>
      )
    },
    filter: 'text',
    width: 110,
    minResizableWidth: 110,
    show: true,
    filterable: true,
    sortable: false,
  },
  {
    title: 'Trading Account',
    field: DealDtoKey.tradingAccount,
    headerCell: HeaderSortCell,
    filterCell: InputFilterCell,
    cell: (props: GridCellProps): JSX.Element => {
      const { tradingAccount } = props.dataItem

      if (!tradingAccount) {
        return <td />
      }

      return (
        <td
          className='table-columns__content'
          style={{ color: 'var(--link)', textDecoration: 'underline' }}
        >
          <Link
            to={{
              pathname: generatePath(links.recordsTradingAccounts.detailsUrl as string, {
                id: tradingAccount.id,
                slug: 'details',
              }),
            }}
          >
            {tradingAccount.name}
          </Link>
        </td>
      )
    },
    width: 150,
    minResizableWidth: 150,
    show: true,
    filterable: true,
    sortable: true,
  },
  {
    title: 'Client',
    field: DealDtoKey.client,
    headerCell: HeaderSortCell,
    cell: (props: GridCellProps): JSX.Element => {
      const { client } = props.dataItem

      return (
        <td
          className='table-columns__content'
          style={{ color: 'var(--link)', textDecoration: 'underline' }}
        >
          <Link
            to={{
              pathname: generatePath(links.recordsClients.detailsUrl as string, {
                id: client.id,
                slug: 'details',
              }),
            }}
          >
            {client.name}
          </Link>
        </td>
      )
    },
    filter: 'text',
    width: 220,
    minResizableWidth: 220,
    show: true,
    filterable: false,
    sortable: true,
  },
  {
    title: 'Instrument',
    field: DealDtoKey.instrument,
    headerCell: HeaderSortCell,
    className: 'table-columns__content',
    filter: 'text',
    width: 120,
    minResizableWidth: 120,
    show: true,
    filterable: false,
    sortable: true,
  },
  {
    title: 'Volume',
    field: DealDtoKey.volume,
    headerCell: HeaderSortCell,
    className: 'table-columns__number',
    filter: 'text',
    width: 100,
    minResizableWidth: 100,
    show: true,
    filterable: false,
    sortable: true,
  },
  {
    title: 'Amount',
    field: DealDtoKey.amount,
    headerCell: HeaderSortCell,
    cell: (props: GridCellProps): JSX.Element => {
      const { amount } = props.dataItem
      if (amount === 0) {
        return <td className='table-columns__number'>0.00</td>
      }
      if (amount === 1) {
        return <td className='table-columns__number'>1.00</td>
      }
      if (amount > 1) {
        return <td className='table-columns__number'>{amount}</td>
      }
      return (
        <td className='table-columns__number'>{parseFloat(props.dataItem.amount).toFixed(4)}</td>
      )
    },
    className: 'table-columns__number',
    filter: 'text',
    width: 100,
    minResizableWidth: 100,
    show: true,
    filterable: false,
    sortable: true,
  },
  {
    title: 'Wallet Transaction',
    field: DealDtoKey.walletTransactionReference,
    cell: (props: GridCellProps): JSX.Element => {
      const { walletTransactionId, walletTransactionReference } = props.dataItem

      if (!walletTransactionId) {
        return <td />
      }

      return (
        <td
          className='table-columns__content'
          style={{ color: 'var(--link)', textDecoration: 'underline' }}
        >
          <Link
            to={{
              pathname: generatePath('/transactions/:id', {
                id: walletTransactionId,
              }),
            }}
          >
            {walletTransactionReference}
          </Link>
        </td>
      )
    },
    className: 'table-columns__content',
    filter: 'text',
    width: 180,
    minResizableWidth: 180,
    show: true,
    filterable: false,
    sortable: false,
  },
  {
    title: 'Date Imported',
    field: DealDtoKey.createdAt,

    cell: (props: GridCellProps) => (
      <td className='table-columns__date'>{formatLocalDateUTC(props.dataItem.createdAt, true)}</td>
    ),
    filter: 'text',
    minResizableWidth: 120,
    width: 120,
    show: true,
    filterable: false,
    sortable: false,
  },
  {
    title: 'Date Closed',
    field: DealDtoKey.tradedAt,
    headerCell: HeaderSortCell,
    cell: (props: GridCellProps) => (
      <td className='table-columns__date'>{formatLocalDateUTC(props.dataItem.tradedAt, true)}</td>
    ),
    filter: 'text',
    minResizableWidth: 150,
    width: 150,
    show: true,
    filterable: false,
    sortable: true,
  },
  {
    title: 'Entity',
    field: DealDtoKey.tickmillCompany,
    cell: (props: GridCellProps): JSX.Element => {
      const { tickmillCompany } = props.dataItem

      if (!tickmillCompany?.id) {
        return <td />
      }

      return (
        <td>
          <CompanyTag id={tickmillCompany.id}>{tickmillCompany.name}</CompanyTag>
        </td>
      )
    },
    filter: 'text',
    width: 80,
    minResizableWidth: 80,
    show: true,
    filterable: false,
    sortable: false,
  },
]
